import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import LaunchCard from "../LaunchCard.jsx";
import HoverTooltip from "../HoverTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { themeShares, methodLanes, radarGridCols } from "../../lib/anatomyDiscovery.js";
import { readDiscovery, subscribeDiscovery } from "../../lib/anatomyDiscoveryStore.js";

// Anatomy › Discover panel — the reader and the run gate for the discovery
// scout (lib/anatomyDiscovery). The report is DURABLE (anatomyDiscoveryStore,
// one entry per scopeKey); the panel renders the store first and treats the
// run as maintenance on it, the same stance as the wiki panel. It renders in
// the page flow (the anatomy body owns the scroll), so its sections align
// with the hero and the Structure tab's blocks.
//
// The report draws before it narrates: themes render as a 100%-stacked bar
// sized by the scout's MEASURED event counts (weight-point fallback for
// reports without counts — never a mix of measured and invented widths), the
// spread as per-endpoint volume bars on one shared scale. Prose is the detail
// layer under a selected segment, not the page. Numbers shown come from the
// report's query-copied counts, and every claim keeps its basis; the methods
// trail at the bottom is the run's audit record.

const EYEBROW = "text-[11px] leading-[1.08] tracking-[0.06em] uppercase font-medium text-[color:var(--eyebrow)]";
const CHIP = "shrink-0 rounded-[2px] border border-[color:var(--line)] px-[6px] py-[2px] text-[10px] leading-[1.2] uppercase tracking-[0.04em] text-[color:var(--muted)]";
const PROSE_MAX = "max-w-[880px]";

// Single-hue ordinal fill: weight is magnitude, so one hue stepped by alpha
// (identity never rides on color — every segment carries its name in ink).
const WEIGHT_FILL = { dominant: 38, major: 22, minor: 10 };

const WHEN_FMT = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const when = (ms) => (Number.isFinite(ms) ? WHEN_FMT.format(new Date(ms)) : "–");

function ActionChip({ label, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`${CHIP} cursor-pointer transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]`}
        >
            {label}
        </button>
    );
}

function SectionHeader({ title, children }) {
    return (
        <div className="flex items-baseline gap-[8px] border-b border-[color:var(--line-soft)] pb-[4px]">
            <span className={EYEBROW}>{title}</span>
            {children}
        </div>
    );
}

// An entity name, clickable when its claim carries a validated target. The
// affordance is the hover underline hugging the text (w-fit) — never a
// persistent rule across the container.
function TargetLink({ target, onNavigateTarget, children, className, style }) {
    if (!target || !onNavigateTarget) {
        return <span className={className} style={style}>{children}</span>;
    }
    return (
        <button
            type="button"
            onClick={() => onNavigateTarget(target)}
            className={`${className} w-fit max-w-full cursor-pointer text-left border-b border-transparent transition-colors hover:border-[color:var(--line-strong)]`}
            style={style}
        >
            {children}
        </button>
    );
}

// PROSE_MAX doubles as an intrinsic-width ceiling: an uncapped one-line basis
// contributes its full unwrapped length (often >1300px) to every ancestor's
// max-content size.
function BasisLine({ basis }) {
    if (!basis) return null;
    return <span className={`${NOTE_CLASS} break-words ${PROSE_MAX}`}>basis: {basis}</span>;
}

// The theme bar: one 100%-stacked strip, segment width = the theme's share,
// fill strength = its weight. Click selects; the detail card below carries
// the prose. 2px gaps let the page surface separate the fills.
function ThemeBar({ segments, selected, onSelect }) {
    return (
        <div className="flex h-[46px] gap-[2px]">
            {segments.map((t, i) => (
                <HoverTooltip
                    key={i}
                    wide
                    placement="top"
                    label={<span>{t.name}{t.description ? ` — ${t.description}` : ""}</span>}
                >
                    <button
                        type="button"
                        onClick={() => onSelect(i)}
                        aria-pressed={selected === i}
                        className={[
                            // w-0 is load-bearing: flex-basis 0 + grow governs the real
                            // width, but without it each segment's INTRINSIC max-content
                            // is its full untruncated label — the bar then demands the
                            // sum of every theme name (~1800px+) from any ancestor whose
                            // width constraint fails, and drags the whole page wide.
                            "w-0 min-w-[46px] overflow-hidden rounded-[2px] border px-[7px] py-[5px] text-left cursor-pointer transition-colors",
                            selected === i
                                ? "border-[color:var(--green)]"
                                : "border-[color:var(--line)] hover:border-[color:var(--line-strong)]",
                        ].join(" ")}
                        style={{
                            flex: `${Math.max(t.share, 0.02)} 1 0%`,
                            background: `color-mix(in srgb, var(--green) ${WEIGHT_FILL[t.weight] ?? 9}%, transparent)`,
                        }}
                    >
                        <span className="block truncate text-[11px] leading-[1.35] text-[color:var(--ink)]">{t.name}</span>
                        <span className="block truncate font-mono text-[10px] leading-[1.35] text-[color:var(--muted)]">
                            {Number.isFinite(t.events) ? formatCompact(t.events) : t.weight}
                        </span>
                    </button>
                </HoverTooltip>
            ))}
        </div>
    );
}

function ThemeDetail({ theme, onNavigateTarget }) {
    if (!theme) return null;
    return (
        <div className="grid min-w-0 gap-[4px] rounded-[2px] border border-[color:var(--line)] px-[10px] py-[8px]">
            <span className="flex min-w-0 flex-wrap items-baseline gap-[8px]">
                <span className="text-[12.5px] leading-[1.35] font-medium text-[color:var(--ink)]">{theme.name}</span>
                <span className={CHIP}>{theme.weight}</span>
                {Number.isFinite(theme.events) ? (
                    <span className="font-mono text-[10.5px] text-[color:var(--muted)]">{formatCompact(theme.events)} events</span>
                ) : null}
                {Number.isFinite(theme.endpointCount) ? (
                    <span className="font-mono text-[10.5px] text-[color:var(--muted)]">{formatCompact(theme.endpointCount)} endpoint{theme.endpointCount === 1 ? "" : "s"}</span>
                ) : null}
                {theme.target ? (
                    <span className="ml-auto">
                        <ActionChip label="Open in Explore" onClick={() => onNavigateTarget?.(theme.target)} />
                    </span>
                ) : null}
            </span>
            {theme.description ? (
                <span className={`text-[11.5px] leading-[1.55] text-[color:var(--muted)] break-words ${PROSE_MAX}`}>{theme.description}</span>
            ) : null}
            <BasisLine basis={theme.basis} />
        </div>
    );
}

// ── Spread radars ──
//
// The generalist/specialist distinction is a claim about the SHAPE of an
// endpoint's work — spread across the scope's themes vs concentrated in one —
// so each endpoint draws as a small radar over the report's themes (shared
// axes, report order). Every radar is normalized to its own busiest theme:
// the polygon shows shape (round = generalist, spiked = specialist), and
// volume is the number under the name, never the polygon area.

const RADAR_SIZE = 116;
const RADAR_R = 40;

function radarPoint(i, n, r, cx, cy) {
    const a = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function radarPolygon(values, n, R, cx, cy) {
    return values
        .map((v, i) => radarPoint(i, n, R * v, cx, cy).map((c) => c.toFixed(1)).join(","))
        .join(" ");
}

// values: one 0..1 radius per axis, in axis order. `size` scales the whole
// drawing — the viewBox stays fixed, so spokes, fills, and numerals scale
// together with the grid cell that hosts them.
function Radar({ values, size = RADAR_SIZE }) {
    const n = values.length;
    const cx = RADAR_SIZE / 2;
    const cy = RADAR_SIZE / 2;
    const outer = radarPolygon(values.map(() => 1), n, RADAR_R, cx, cy);
    return (
        <svg width={size} height={size} viewBox={`0 0 ${RADAR_SIZE} ${RADAR_SIZE}`} aria-hidden="true">
            {values.map((_, i) => {
                const [x, y] = radarPoint(i, n, RADAR_R, cx, cy);
                return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line-soft)" strokeWidth="1" />;
            })}
            <polygon points={outer} fill="none" stroke="var(--line)" strokeWidth="1" />
            <polygon
                points={radarPolygon(values, n, RADAR_R, cx, cy)}
                style={{ fill: "color-mix(in srgb, var(--green) 22%, transparent)" }}
                stroke="var(--green)"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
            {values.map((v, i) => {
                const [x, y] = radarPoint(i, n, RADAR_R + 7, cx, cy);
                return (
                    <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize="7" fill={v > 0 ? "var(--muted)" : "var(--muted-soft)"}>
                        {i + 1}
                    </text>
                );
            })}
        </svg>
    );
}

function RadarCard({ entry, axes, onNavigateTarget, size = RADAR_SIZE }) {
    // Stored reports predating themeMix carry no field at all — the store
    // never migrates persisted entries, so absence must read as unmeasured.
    const mix = Array.isArray(entry.themeMix) ? entry.themeMix : [];
    const byName = new Map(mix.map((m) => [m.name, m.events]));
    const raw = axes.map((a) => byName.get(a) || 0);
    const own = Math.max(...raw, 1);
    const measured = mix.length > 0;
    const body = (
        <div className="grid justify-items-center gap-[2px]">
            {measured ? (
                <Radar values={raw.map((v) => v / own)} size={size} />
            ) : (
                <div
                    className="grid place-items-center rounded-full border border-dashed border-[color:var(--line)]"
                    style={{ width: size - 24, height: size - 24, margin: 12 }}
                >
                    <span className={`${NOTE_CLASS} px-[8px] text-center`}>no mix measured</span>
                </div>
            )}
            <TargetLink
                target={entry.target}
                onNavigateTarget={onNavigateTarget}
                className="truncate text-[11.5px] leading-[1.4] text-[color:var(--ink)]"
                style={{ maxWidth: size + 14 }}
            >
                {entry.label}
            </TargetLink>
            <span className="font-mono text-[10px] leading-[1.3] text-[color:var(--muted)]">
                {Number.isFinite(entry.events) ? `${formatCompact(entry.events)} events` : ""}
            </span>
        </div>
    );
    const tip = (
        <span>
            {entry.note ? <>{entry.note}<br /></> : null}
            {measured
                ? mix.map((m, i) => (
                    <span key={i} className="font-mono text-[10.5px]">{m.name} — {formatCompact(m.events)}<br /></span>
                ))
                : "per-theme mix not measured on this run"}
        </span>
    );
    return (
        <HoverTooltip wide maxWidth={360} placement="top" label={tip}>
            <div className="w-fit">{body}</div>
        </HoverTooltip>
    );
}

// Shared axis legend: spoke number → theme name, in the report's theme order.
// shrink-0 is load-bearing: a shrinkable truncating flex item squeezes onto
// one line instead of wrapping, crushing the tail items to nothing.
function RadarLegend({ axes }) {
    return (
        <div className="flex min-w-0 flex-wrap gap-x-[12px] gap-y-[2px]">
            {axes.map((a, i) => (
                <span key={i} className={`${NOTE_CLASS} shrink-0 truncate max-w-[240px]`}>
                    <span className="font-mono text-[color:var(--muted)]">{i + 1}</span> {a}
                </span>
            ))}
        </div>
    );
}

const RADAR_CELL_MIN = 150;
const RADAR_CELL_MAX = 230;
const RADAR_GRID_GAP = 14;
const RADAR_DRAW_MAX = 200;

function SpreadRadars({ axes, generalists, specialists, onNavigateTarget }) {
    const lanes = [
        { heading: "Generalists", entries: generalists },
        { heading: "Specialist archetypes", entries: specialists },
    ].filter((l) => l.entries.length);
    // Measured width drives a DELIBERATE column count (radarGridCols): fill
    // the row, never strand a lone orphan on the last row, one rhythm shared
    // by both lanes. Unmeasured (first paint, jsdom) falls back inside the
    // chooser.
    const [gridW, setGridW] = useState(0);
    const [gridEl, setGridEl] = useState(null);
    useEffect(() => {
        if (!gridEl) return undefined;
        const read = () => setGridW(gridEl.clientWidth);
        read();
        const ro = typeof ResizeObserver === "function" ? new ResizeObserver(read) : null;
        ro?.observe(gridEl);
        return () => ro?.disconnect();
    }, [gridEl]);
    if (!lanes.length || axes.length < 3) return null;

    const cols = radarGridCols({
        width: gridW,
        counts: lanes.map((l) => l.entries.length),
        minCell: RADAR_CELL_MIN,
        gap: RADAR_GRID_GAP,
    });
    // Cards scale WITH their tracks — a wide panel gets larger radars, not a
    // wider prairie — capped so five endpoints never become five dinner plates.
    const trackW = gridW > 0 ? (gridW - RADAR_GRID_GAP * (cols - 1)) / cols : RADAR_CELL_MIN;
    const cardW = Math.min(trackW, RADAR_CELL_MAX);
    const drawSize = Math.round(Math.max(RADAR_SIZE, Math.min(cardW - 30, RADAR_DRAW_MAX)));

    return (
        <div className="grid min-w-0 gap-[10px]">
            <RadarLegend axes={axes} />
            {/* ONE grid for every lane: headers span the full row, cards from
                different lanes land on identical tracks. */}
            <div
                ref={setGridEl}
                className="grid min-w-0 justify-items-center gap-y-[12px]"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, columnGap: RADAR_GRID_GAP }}
            >
                {lanes.map((lane) => (
                    <Fragment key={lane.heading}>
                        <span className={`${EYEBROW} col-span-full justify-self-start pt-[3px]`}>{lane.heading}</span>
                        {lane.entries.map((e, i) => (
                            <RadarCard key={i} entry={e} axes={axes} onNavigateTarget={onNavigateTarget} size={drawSize} />
                        ))}
                    </Fragment>
                ))}
            </div>
            <span className={NOTE_CLASS}>
                Each radar is normalized to its busiest theme. The polygon shows the mix of work; the number shows volume.
            </span>
        </div>
    );
}

function PocketCard({ pocket, onNavigateTarget }) {
    return (
        <div className="grid min-w-0 content-start gap-[3px] rounded-[2px] border border-[color:var(--line)] px-[10px] py-[8px]">
            <TargetLink
                target={pocket.target}
                onNavigateTarget={onNavigateTarget}
                className="min-w-0 text-[12px] leading-[1.35] font-medium text-[color:var(--ink)]"
            >
                {pocket.name}
            </TargetLink>
            {pocket.description ? (
                <span className={`text-[11.5px] leading-[1.5] text-[color:var(--muted)] break-words ${PROSE_MAX}`}>{pocket.description}</span>
            ) : null}
            {pocket.whyNotable ? (
                <span className={`text-[11.5px] leading-[1.5] text-[color:var(--ink)] break-words ${PROSE_MAX}`}>Why it matters: {pocket.whyNotable}</span>
            ) : null}
            <BasisLine basis={pocket.basis} />
        </div>
    );
}

// ── Method lanes ──
//
// The scout run rendered as a trace, in the trace-detail Lanes view's own
// vocabulary (mirrored locally — surfaces keep their mark primitives private):
// lanes = the entities the scout queried, columns = the query sequence, a
// scout lane carries the narration beats. Square marks are work on a subject
// (hollow = a query, red = it errored); diamonds are beats of the scout's own
// commentary — shape keeps the two apart at a glance, same as the Lanes tab.

// Geometry and zoom bounds mirror TraceLanesView: cellW is the zoom (px per
// step, fractional at fit), marks bucket below LANE_MARK_MIN_PX.
const LANE_CELL_1X = 18;
const LANE_CELL_MAX = 26;
const LANE_MARK_MIN_PX = 7;
const LANE_ROW_H = 24;
const LANE_RAIL_W = 172;
const ZOOM_BTN = "grid h-[16px] min-w-[16px] place-items-center rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[3px] font-mono text-[10px] leading-none text-[color:var(--muted)] cursor-pointer hover:text-[color:var(--ink)]";

function LaneMark({ error, count = 1 }) {
    const color = error ? "var(--red,#c0392b)" : "var(--muted)";
    if (count > 1) {
        return (
            <span
                className="grid h-[13px] min-w-[13px] place-items-center rounded-[2px] border px-[2px] font-mono text-[8px] leading-none"
                style={{
                    color,
                    borderColor: `color-mix(in srgb, ${color} 60%, var(--line))`,
                    background: error ? `color-mix(in srgb, ${color} 18%, transparent)` : "transparent",
                }}
            >
                {count}
            </span>
        );
    }
    return (
        <span
            className="h-[8px] w-[8px] rounded-[1px] border"
            style={{ borderColor: color, background: error ? color : "transparent" }}
        />
    );
}

function LaneLegend() {
    return (
        <span className="flex flex-wrap items-center gap-x-[10px] gap-y-[3px]">
            <span className="flex items-center gap-[5px] text-[10px] leading-none text-[color:var(--muted)]"><LaneMark /> query</span>
            <span className="flex items-center gap-[5px] text-[10px] leading-none text-[color:var(--muted)]"><LaneMark error /> errored</span>
            <span className="flex items-center gap-[5px] text-[10px] leading-none text-[color:var(--muted)]">
                <span className="text-[10px] leading-none" style={{ color: "var(--eyebrow)" }}>◆</span> narration
            </span>
            <span className="text-[10px] leading-none text-[color:var(--muted-soft)]">⌘-scroll zooms</span>
        </span>
    );
}

// Bucketing, same rule as the trace lanes: one bucket per column while a
// column is at least LANE_MARK_MIN_PX wide, else spans of columns merge into
// a count chip. Never drops a step — zooming out trades position for count.
function bucketMarks(marks, cellW) {
    const span = cellW >= LANE_MARK_MIN_PX ? 1 : Math.ceil(LANE_MARK_MIN_PX / cellW);
    const buckets = new Map();
    for (const m of marks) {
        const b = Math.floor(m.col / span);
        let cur = buckets.get(b);
        if (!cur) {
            cur = { start: b * span, items: [], error: false };
            buckets.set(b, cur);
        }
        cur.items.push(m);
        cur.error = cur.error || Boolean(m.error);
    }
    return [...buckets.values()].map((b) => ({ ...b, x: b.start * cellW + (span * cellW) / 2 }));
}

function stepLine(m) {
    return `${m.name}${m.summary ? ` · ${m.summary}` : ""}${m.error ? " · ERROR" : Number.isFinite(m.rows) ? ` · ${m.rows} row${m.rows === 1 ? "" : "s"}` : ""}`;
}

const isNumericCell = (v) => v === "" || typeof v === "number" || /^-?\d+(\.\d+)?$/.test(String(v));

// The picked query's ACTUAL result — the same rows the scout read, as a mini
// table. The right-most all-numeric column also draws an inline magnitude bar
// so an aggregate result reads as a chart, not just digits. Scrolls inside
// its own box; cells are pre-capped by the trail builder.
function StepResultTable({ preview, cap = 20 }) {
    if (!preview?.rows?.length) return null;
    const rows = preview.rows.slice(0, cap);
    const colCount = Math.max(preview.columns.length, ...rows.map((r) => r.length));
    const numeric = Array.from({ length: colCount }, (_, i) => rows.every((r) => isNumericCell(r[i] ?? "")));
    let barCol = -1;
    for (let i = colCount - 1; i >= 0; i--) {
        if (numeric[i] && rows.some((r) => Number(r[i]) > 0)) { barCol = i; break; }
    }
    const barMax = barCol >= 0 ? Math.max(...rows.map((r) => Number(r[barCol]) || 0), 1) : 1;
    return (
        <div className="grid min-w-0 gap-[3px]">
            <div className="min-w-0 max-h-[240px] overflow-auto rounded-[2px] border border-[color:var(--line-soft)]">
                <table className="border-separate border-spacing-0 font-mono text-[10.5px] leading-[1.5]">
                    <thead>
                        <tr>
                            {Array.from({ length: colCount }, (_, i) => (
                                <th key={i} className={`sticky top-0 bg-[color:var(--bg)] px-[8px] py-[3px] text-[9px] uppercase tracking-[0.05em] text-[color:var(--muted-soft)] ${numeric[i] ? "text-right" : "text-left"}`}>
                                    {preview.columns[i] || `col ${i + 1}`}
                                </th>
                            ))}
                            {barCol >= 0 ? <th className="sticky top-0 bg-[color:var(--bg)]" /> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, ri) => (
                            <tr key={ri}>
                                {Array.from({ length: colCount }, (_, ci) => (
                                    <td key={ci} className={`border-t border-[color:var(--line-soft)] px-[8px] py-[2px] ${numeric[ci] ? "text-right text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>
                                        <span className="block max-w-[280px] truncate">{numeric[ci] && r[ci] !== "" ? formatCompact(Number(r[ci])) : String(r[ci] ?? "")}</span>
                                    </td>
                                ))}
                                {barCol >= 0 ? (
                                    <td className="border-t border-[color:var(--line-soft)] py-[2px] pl-[2px] pr-[8px] align-middle">
                                        <span className="block h-[6px] w-[88px] rounded-[1px] bg-[color:var(--control-bg)]">
                                            <span
                                                className="block h-full rounded-[1px]"
                                                style={{
                                                    width: `${Math.max(((Number(r[barCol]) || 0) / barMax) * 100, Number(r[barCol]) > 0 ? 2 : 0)}%`,
                                                    background: "color-mix(in srgb, var(--green) 32%, transparent)",
                                                }}
                                            />
                                        </span>
                                    </td>
                                ) : null}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {preview.total > rows.length ? (
                <span className={NOTE_CLASS}>+{preview.total - rows.length} more row{preview.total - rows.length === 1 ? "" : "s"}. The scout read the full set.</span>
            ) : null}
        </div>
    );
}

// The scout run as a zoomable trace: same mechanics as the trace-detail
// Lanes view — cellW zoom via −/+/Fit/1:1 or ⌘/ctrl-scroll anchored at the
// cursor, fit as the opening frame, marks bucketing when columns get too
// narrow to draw, and click-to-read: a mark opens that step's full text in
// the strip below (which is why there is no separate plain-text trail).
function MethodLanesChart({ trail }) {
    const { lanes, cols, narration } = useMemo(() => methodLanes(trail), [trail]);
    const [cellW, setCellW] = useState(LANE_CELL_1X);
    const [sel, setSel] = useState(null); // { label, items } — items carry note text for the scout lane
    const userZoomedRef = useRef(false);
    const pendingScrollRef = useRef(null);
    const scrollElRef = useRef(null);
    const [scrollEl, setScrollEl] = useState(null);
    scrollElRef.current = scrollEl;
    const [viewW, setViewW] = useState(0);
    // Fit FILLS the available width — a method run has tens of columns, not
    // hundreds of turns, so unlike the trace lanes fit usually zooms IN and
    // must be allowed past the nominal cell cap. The zoom range widens to
    // reach it: out to 1:1 (or fit, when fit is narrower), in to fit.
    const fitRef = useRef(LANE_CELL_1X);
    const minZoomRef = useRef(2);
    const maxZoomRef = useRef(LANE_CELL_MAX);
    if (viewW > 40 && cols > 0) {
        fitRef.current = Math.max(2, (viewW - 2) / cols);
        minZoomRef.current = Math.max(2, Math.min(fitRef.current, LANE_CELL_1X));
        maxZoomRef.current = Math.max(LANE_CELL_MAX, fitRef.current);
    }

    useEffect(() => {
        const el = scrollEl;
        if (!el) return undefined;
        const read = () => setViewW(el.clientWidth);
        read();
        const ro = typeof ResizeObserver === "function" ? new ResizeObserver(read) : null;
        ro?.observe(el);
        return () => ro?.disconnect();
    }, [scrollEl]);

    // First useful measure → open at fit, so the run's whole shape is the
    // opening frame and zooming IN is the deliberate act.
    useEffect(() => {
        if (userZoomedRef.current || viewW <= 40 || !cols) return;
        setCellW(fitRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewW > 40, cols]);

    const zoomAt = useCallback((factor, viewportOffset = null) => {
        userZoomedRef.current = true;
        const el = scrollElRef.current;
        setCellW((cur) => {
            const next = Math.max(minZoomRef.current, Math.min(maxZoomRef.current, cur * factor));
            if (el && next !== cur) {
                const off = viewportOffset ?? el.clientWidth / 2;
                const step = (el.scrollLeft + off) / cur;
                pendingScrollRef.current = Math.max(0, step * next - off);
            }
            return next;
        });
    }, []);
    const zoomTo = useCallback((value) => {
        userZoomedRef.current = true;
        setCellW(Math.max(minZoomRef.current, Math.min(maxZoomRef.current, value)));
    }, []);

    const width = Math.max(1, Math.ceil(cols * cellW));
    // Apply the zoom-anchored scroll correction in the same layout pass that
    // applies the new axis width.
    useLayoutEffect(() => {
        const el = scrollElRef.current;
        if (el && pendingScrollRef.current != null) {
            el.scrollLeft = pendingScrollRef.current;
            pendingScrollRef.current = null;
        }
    }, [width, cellW]);

    // ⌘/ctrl-scroll (and trackpad pinch, which arrives as ctrl+wheel) zooms at
    // the cursor. Native listener: React's synthetic wheel can't preventDefault.
    useEffect(() => {
        const el = scrollEl;
        if (!el) return undefined;
        const onWheel = (e) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            zoomAt(Math.exp(-e.deltaY * 0.0022), e.clientX - rect.left);
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [scrollEl, zoomAt]);

    if (!cols) return null;

    const railRow = "flex h-[24px] items-center gap-[6px] border-b border-[color:var(--line-soft)]";
    const selCol = sel?.items?.length ? sel.items[0].col : null;
    const rulerStep = (() => {
        for (const s of [1, 2, 5, 10, 20]) {
            if (s * cellW >= 34) return s;
        }
        return 50;
    })();

    return (
        <div className="grid min-w-0 gap-[8px]">
            <div className="flex min-w-0 flex-wrap items-center gap-x-[10px] gap-y-[3px]">
                <span className="flex items-center gap-[4px]">
                    <button type="button" className={ZOOM_BTN} onClick={() => zoomAt(1 / 1.5)} aria-label="Zoom out">−</button>
                    <button type="button" className={ZOOM_BTN} onClick={() => zoomAt(1.5)} aria-label="Zoom in">+</button>
                    <button type="button" className={ZOOM_BTN} onClick={() => zoomTo(fitRef.current)} aria-label="Zoom to fit">Fit</button>
                    <button type="button" className={ZOOM_BTN} onClick={() => zoomTo(LANE_CELL_1X)} aria-label="Zoom to one step per column">1:1</button>
                </span>
                <span className="min-w-0 flex-1" />
                <LaneLegend />
            </div>
            <div className="flex min-w-0">
                {/* Rail: subject labels + per-lane totals, fixed width so the
                    timeline owns all remaining room. */}
                <div className="shrink-0" style={{ width: LANE_RAIL_W }}>
                    <div className="h-[16px]" />
                    <div className={railRow}>
                        <span className="min-w-0 truncate font-mono text-[10.5px] text-[color:var(--ink)]">scout</span>
                        <span className="ml-auto shrink-0 pr-[6px] font-mono text-[9.5px] text-[color:var(--muted-soft)]">{narration.length} note{narration.length === 1 ? "" : "s"}</span>
                    </div>
                    {lanes.map((lane) => (
                        <div key={lane.label} className={railRow}>
                            <span className="min-w-0 truncate font-mono text-[10.5px] text-[color:var(--ink)]">{lane.label}</span>
                            <span className="ml-auto shrink-0 pr-[6px] font-mono text-[9.5px] text-[color:var(--muted-soft)]">
                                {lane.queries}q{lane.rows > 0 ? ` · ${formatCompact(lane.rows)} rows` : ""}
                            </span>
                        </div>
                    ))}
                </div>
                {/* Timeline: ruler + one row per lane, one shared pan/zoom region. */}
                <div ref={setScrollEl} className="min-w-0 flex-1 overflow-x-auto">
                    <div className="relative" style={{ width }}>
                        {selCol != null ? (
                            <span
                                className="pointer-events-none absolute bottom-0 top-0 w-[1px]"
                                style={{ left: selCol * cellW + cellW / 2, background: "color-mix(in srgb, var(--green) 45%, transparent)" }}
                            />
                        ) : null}
                        <div className="relative h-[16px]">
                            {Array.from({ length: Math.ceil(cols / rulerStep) }, (_, i) => i * rulerStep).map((c) => (
                                <span
                                    key={c}
                                    className="absolute top-0 -translate-x-1/2 font-mono text-[8px] leading-[16px] text-[color:var(--muted-soft)]"
                                    style={{ left: c * cellW + cellW / 2 }}
                                >
                                    {c}
                                </span>
                            ))}
                        </div>
                        <div className="relative border-b border-[color:var(--line-soft)]" style={{ height: LANE_ROW_H }}>
                            {bucketMarks(narration, cellW).map((b, i) => (
                                <HoverTooltip key={i} wide maxWidth={420} placement="top" label={<span>{b.items.length > 1 ? `${b.items.length} notes. Select to read.` : `${b.items[0].thinking ? "thinking · " : ""}${b.items[0].text}`}</span>}>
                                    <button
                                        type="button"
                                        aria-label={`read the scout's narration at step ${b.items[0].col}`}
                                        onClick={() => setSel({ label: "scout", items: b.items })}
                                        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer p-[3px] text-[10px] leading-none"
                                        style={{ left: b.x, color: "var(--eyebrow)" }}
                                    >
                                        {b.items.length > 1 ? `◆${b.items.length}` : "◆"}
                                    </button>
                                </HoverTooltip>
                            ))}
                        </div>
                        {lanes.map((lane) => (
                            <div key={lane.label} className="relative border-b border-[color:var(--line-soft)]" style={{ height: LANE_ROW_H }}>
                                {bucketMarks(lane.marks, cellW).map((b, i) => (
                                    <HoverTooltip
                                        key={i}
                                        wide
                                        maxWidth={420}
                                        placement="top"
                                        label={<span className="font-mono text-[10.5px]">{b.items.length > 1 ? `${b.items.length} queries. Select to read.` : stepLine(b.items[0])}</span>}
                                    >
                                        <button
                                            type="button"
                                            aria-label={`read ${lane.label} at step ${b.items[0].col}`}
                                            onClick={() => setSel({ label: lane.label, items: b.items })}
                                            className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2 cursor-pointer p-[3px]"
                                            style={{ left: b.x }}
                                        >
                                            <LaneMark error={b.error} count={b.items.length} />
                                        </button>
                                    </HoverTooltip>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* The reading strip — the dock: a picked mark's step(s), verbatim. */}
            {sel ? (
                <div className="grid min-w-0 gap-[3px] rounded-[2px] border border-[color:var(--line)] px-[10px] py-[7px]">
                    <span className="flex items-baseline gap-[8px]">
                        <span className={EYEBROW}>{sel.label}</span>
                        <span className="font-mono text-[9.5px] text-[color:var(--muted-soft)]">step {sel.items[0].col}{sel.items.length > 1 ? `–${sel.items[sel.items.length - 1].col}` : ""}</span>
                        <span className="ml-auto"><ActionChip label="Close" onClick={() => setSel(null)} /></span>
                    </span>
                    {sel.label === "scout"
                        ? sel.items.map((n, i) => (
                            <span key={i} className={`${NOTE_CLASS} break-words ${PROSE_MAX}`}>{n.thinking ? "thinking · " : ""}{n.text}</span>
                        ))
                        : sel.items.map((m, i) => (
                            <div key={i} className="grid min-w-0 gap-[3px]">
                                <span className={`font-mono text-[10.5px] leading-[1.5] text-[color:var(--muted)] break-words ${PROSE_MAX}`}>{stepLine(m)}</span>
                                <StepResultTable preview={m.preview} cap={sel.items.length > 1 ? 6 : 20} />
                            </div>
                        ))}
                </div>
            ) : (
                <span className={NOTE_CLASS}>Select a mark to read the scout's query or narration.</span>
            )}
        </div>
    );
}

// The method as a trace: the scout's investigation in the Lanes view's
// grammar — subjects × sequence, zoomable, with click-to-read marks. The
// reading strip below the chart replaces any separate plain-text trail, and
// "Open as trace" opens the run itself in the trace sidebar (the captured
// body resolves through getTrace's synthetic-id seam).
function MethodsTrail({ trail, onOpenTrace }) {
    if (!trail?.length) return null;
    const steps = trail.reduce((n, g) => n + (g.steps?.length || 0), 0);
    return (
        <div className="grid min-w-0 gap-[8px]">
            <SectionHeader title="Method">
                <span className={NOTE_CLASS}>{steps} quer{steps === 1 ? "y" : "ies"}</span>
                {onOpenTrace ? (
                    <span className="ml-auto"><ActionChip label="Open as trace" onClick={onOpenTrace} /></span>
                ) : null}
            </SectionHeader>
            <MethodLanesChart trail={trail} />
        </div>
    );
}

function RunProgress({ run, scopeLabel, onAbort }) {
    const p = run.progress || {};
    const queries = Number.isFinite(p.queries) ? p.queries : 0;
    return (
        <div className="grid justify-items-start gap-[8px]" aria-busy="true">
            <span className={`${NOTE_CLASS} animate-pulse`}>
                Scouting {scopeLabel || "this scope"}: {queries} quer{queries === 1 ? "y" : "ies"} so far…
            </span>
            {p.note ? <span className={`${NOTE_CLASS} break-words ${PROSE_MAX}`}>{p.note}</span> : null}
            <span className={NOTE_CLASS}>
                The scout samples this scope's themes, endpoint spread, and work patterns. Each claim includes its supporting evidence.
            </span>
            <ActionChip label="Stop" onClick={onAbort} />
        </div>
    );
}

export default function AnatomyDiscoverPanel({
    run,
    onRun,
    onAbort,
    scopeKey,
    scopeLabel,
    onNavigateTarget,
    onOpenTrace = null,
    modelControl = null,
}) {
    const [discovery, setDiscovery] = useState(() => readDiscovery());
    useEffect(() => subscribeDiscovery(() => setDiscovery(readDiscovery())), []);
    const entry = useMemo(
        () => discovery.reports?.[scopeKey || "org"] || null,
        [discovery, scopeKey],
    );

    const { mode: shareMode, segments } = useMemo(
        () => themeShares(entry?.report?.themes),
        [entry],
    );
    const [selectedTheme, setSelectedTheme] = useState(0);
    useEffect(() => { setSelectedTheme(0); }, [entry]);

    if (run.status === "running") {
        return <RunProgress run={run} scopeLabel={scopeLabel} onAbort={onAbort} />;
    }

    const errorLine = run.status === "error" ? (
        <span className="font-mono text-[11px] leading-[1.4] text-[color:var(--red,#c0392b)] break-words">{run.error}</span>
    ) : null;

    if (!entry) {
        return (
            <div className="grid gap-[6px]">
                <LaunchCard
                    eyebrow="Discover"
                    description={`Profile ${scopeLabel || "this scope"} from its own traces. A scout samples work themes, activity across endpoints, and specialist areas. Each claim includes its supporting evidence. Structure shows volume; this profile explains the work.`}
                    controls={modelControl ? (
                        <div className="flex min-w-0 items-center gap-[8px]">
                            <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">Model</span>
                            {modelControl}
                        </div>
                    ) : null}
                    ctaLabel={run.status === "error" ? "Retry" : "Profile this scope"}
                    onLaunch={onRun}
                />
                {errorLine}
            </div>
        );
    }

    const { report, trail, meta } = entry;
    const spread = report.spread || {};
    const hasSpread = Boolean(spread.summary || spread.generalists?.length || spread.specialists?.length);

    return (
        <>
            <div className="grid gap-[6px]">
                {report.profile ? (
                    <span className={`text-[12.5px] leading-[1.6] text-[color:var(--ink)] break-words ${PROSE_MAX}`}>{report.profile}</span>
                ) : null}
                <div className="flex flex-wrap items-center gap-[8px]">
                    <ActionChip label={run.status === "error" ? "Retry" : "Regenerate"} onClick={onRun} />
                    {modelControl}
                    <span className={NOTE_CLASS}>
                        profiled {when(meta?.at)}
                        {meta?.rangeText ? ` · over ${meta.rangeText}` : ""}
                        {meta?.model ? <> · by <span className="font-mono">{meta.model}</span></> : null}
                    </span>
                </div>
                {run.notice ? <span className={`${NOTE_CLASS} break-words ${PROSE_MAX}`}>{run.notice}</span> : null}
                {(meta?.scopeNotes || []).map((n, i) => (
                    <span key={i} className={`${NOTE_CLASS} break-words ${PROSE_MAX}`}>{n}</span>
                ))}
                {errorLine}
            </div>

            {segments.length ? (
                <div className="grid gap-[8px]">
                    <SectionHeader title="Themes">
                        {shareMode === "weight" ? (
                            <span className={NOTE_CLASS}>Segments are sized by weight. Regenerate to use measured events.</span>
                        ) : null}
                    </SectionHeader>
                    <ThemeBar segments={segments} selected={selectedTheme} onSelect={setSelectedTheme} />
                    <ThemeDetail theme={report.themes[selectedTheme] || report.themes[0]} onNavigateTarget={onNavigateTarget} />
                </div>
            ) : null}

            {hasSpread ? (
                <div className="grid gap-[8px]">
                    <SectionHeader title="Spread">
                        <span className={CHIP}>{spread.concentration}</span>
                    </SectionHeader>
                    {spread.summary ? (
                        <span className={`text-[11.5px] leading-[1.55] text-[color:var(--muted)] break-words ${PROSE_MAX}`}>{spread.summary}</span>
                    ) : null}
                    <SpreadRadars
                        axes={report.themes.map((t) => t.name)}
                        generalists={spread.generalists || []}
                        specialists={spread.specialists || []}
                        onNavigateTarget={onNavigateTarget}
                    />
                    <BasisLine basis={spread.basis} />
                </div>
            ) : null}

            {report.pockets?.length ? (
                <div className="grid gap-[8px]">
                    <SectionHeader title="Pockets" />
                    <div className="grid gap-[6px] sm:grid-cols-2">
                        {report.pockets.map((p, i) => (
                            <PocketCard key={i} pocket={p} onNavigateTarget={onNavigateTarget} />
                        ))}
                    </div>
                </div>
            ) : null}

            <MethodsTrail trail={trail} onOpenTrace={entry.traceBody && onOpenTrace ? onOpenTrace : null} />
        </>
    );
}
